<template>
  <div class="home-container">
    <div class="home-banner">
      <div class="banner-content">
        <div class="banner-left">
          <img
            v-if="isImageLoaded"
            :src="imageUrl + organization + '.jpg'"
            alt="Organization Logo"
            class="banner-logo"
            @error="handleImageError"
          />
          <a-typography-title v-else :level="2" class="banner-title">
            Organization Summary
          </a-typography-title>
        </div>
        <div class="banner-right">
          <a-button
            type="primary"
            @click="toggleArchivedView"
            class="archive-toggle-button"
            size="large"
            :loading="loading"
          >
            <template #icon>
              <archive-outlined v-if="!isArchived" />
              <eye-outlined v-else />
            </template>
            {{ isArchived ? 'Show Active Devices' : 'Show Archived Devices' }}
          </a-button>
        </div>
      </div>
    </div>
    <div class="cards-container p-lg-4 p-xl-4 p-md-3 p-sm-4 p-2">
      <div class="cards-wrapper px-lg-4 px-xl-4 px-md-3 px-sm-4 px-2">
        <a-row class="pb-4" :gutter="[24, 24]">
          <a-col
            v-for="device in sortedDevices"
            :key="device.Serial_number"
            :xs="24"
            :sm="24"
            :md="24"
            :lg="12"
            :xl="8"
            :xxl="8"
          >
            <work-station-card
              :device="device"
              :active-devices="activeDevices"
              :id-to-task-name-mapping="idToTaskNameMapping"
              :id-to-device-serial-no-mapping="idToDeviceSerialNoMapping"
              :active-devices-settings="activeDevicesSettings"
              :active-devices-status="devicesStats"
              @device-archived="handleDeviceArchived"
              @device-unarchived="handleDeviceUnarchived"
            />
          </a-col>
        </a-row>
        <div
          v-if="sortedDevices.length === 0 && !loading"
          style="height: 70vh"
          class="no-active-device"
        >
          <laptop-outlined class="text-secondary" />
          <a-typography-title :level="3" class="mt-2 text-secondary">
            {{
              isArchived
                ? 'No Archived Device(s) Found'
                : 'No Active Device(s) Found'
            }}
          </a-typography-title>
        </div>

        <!-- Skeleton Loader -->
        <div v-if="loading" class="skeleton-container">
          <a-row :gutter="[24, 24]">
            <a-col
              v-for="n in 6"
              :key="n"
              :xs="24"
              :sm="24"
              :md="24"
              :lg="12"
              :xl="8"
              :xxl="8"
            >
              <a-card class="skeleton-card">
                <a-skeleton active :paragraph="{ rows: 4 }" />
              </a-card>
            </a-col>
          </a-row>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import DeviceService from '@/services/device.js';
import TaskService from '@/services/tasks.js';
import {
  ArchiveOutlined,
  EyeOutlined,
  LaptopOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { dateFormat } from 'src/config/date-format-config.js';
import { inject, ref, watch } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { useActiveDevices } from '../RecordData/composables/useActiveDevices';
import { useDeviceSettings } from '../RecordData/composables/useDeviceSettings';
import WorkStationCard from './WorkStationCard.vue';
dayjs.extend(relativeTime);

export default {
  components: {
    WorkStationCard,
    LaptopOutlined,
    ArchiveOutlined,
    EyeOutlined,
  },
  setup() {
    const toast = inject('toast');
    const deviceRef = ref({});
    const { activeDevices: curentActiveDevices } = useActiveDevices();

    const { devicesSettingsDict, devicesStatusDict } = useDeviceSettings(toast);

    watch(
      () => curentActiveDevices.value,
      (dict) => {
        if (!Object.keys(dict).length) return;
        deviceRef.value = {};
        Object.entries(dict).forEach(([, value]) => {
          deviceRef.value[value?.Serial_number] = value;
        });
      }
    );

    return {
      // activeDevices,
      // activeDevicesList,
      devicesSettingsDict,
      devicesStatusDict,
      activeDevices: deviceRef,
    };
  },
  data() {
    return {
      isArchived: false,
      loading: false,
      deviceList: [],
      archivedDeviceList: [],
      // activeDevices: {},
      // isPolled: false,
      // activeDevicesPolling: null,
      tasksList: [],
      idToTaskNameMapping: {},
      idToDeviceSerialNoMapping: {},
      activeDevicesSettings: [],
      deviceStatsPolling: null,
      devicesStats: {},
      isImageLoaded: true,
      imageUrl:
        'https://retrocausal-video-thumbnails.s3.us-east-2.amazonaws.com/logos/',
    };
  },

  computed: {
    ...mapGetters(['organization', 'devices', 'startedDevice']),
    sortedDevices() {
      const currentList = this.isArchived
        ? this.archivedDeviceList
        : this.deviceList;
      if (currentList.length) {
        const [active, starred, inactive] = this.sortDevices(
          currentList,
          this.sortDevicesCallback
        );
        currentList.map((device) => {
          if (!device.last_Active.endsWith('ago')) {
            if (device.last_Active.endsWith('UTC'))
              device.last_Active = dayjs(
                new Date(device.last_Active).toISOString()
              ).fromNow();
          }
          return device;
        });
        this.sortActiveDevicesByDisplayName(active);
        return [...active, ...starred, ...inactive];
      } else {
        return [];
      }
    },
  },

  watch: {
    startedDevice: {
      handler(val) {
        if (val) {
          this.deviceList = this.deviceList.map((data) => {
            if (val.id == data.id) {
              return { ...data, starred: val.starred };
            }
            return { ...data };
          });

          this.starredDevices(null);
        }
      },
      deep: true,
    },
    devices: {
      handler(val) {
        this.deviceList = [...val];
      },
      deep: true,
    },
    deviceList(list) {
      if (!list.length) return;
      this.idToDeviceSerialNoMapping = this.getIdToAttributeMapOfList(
        list,
        'Serial_number'
      );
    },
    tasksList(tasks) {
      if (!tasks.length) return;
      this.idToTaskNameMapping = this.getIdToAttributeMapOfList(
        tasks,
        'taskName'
      );
    },
    activeDevices(dict) {
      if (dict && Object.keys(dict)?.length) {
        if (!this.deviceStatsPolling) this.setDeviceStatsInterval();
      } else if (this.deviceStatsPolling) {
        clearInterval(this.deviceStatsPolling);
        this.deviceStatsPolling = null;
      }
    },
  },

  async created() {
    await this.checkPermissionsState();
    this.getAllDevices();
    this.getTasksList();
    this.getActiveDevicesStats();
  },

  beforeUnmount() {
    if (this.deviceStatsPolling) clearInterval(this.deviceStatsPolling);
  },

  methods: {
    ...mapActions(['getOrgDevices', 'starredDevices']),
    async toggleArchivedView() {
      this.loading = true;
      const newStatus = !this.isArchived;

      try {
        if (newStatus && this.archivedDeviceList.length === 0) {
          // Only fetch archived devices if we haven't loaded them yet
          await this.getOrgDevices({
            orgName: this.organization,
            isArchived: true,
          });
          // Store the archived devices in our local state
          this.archivedDeviceList = [...this.devices];
        } else if (!newStatus && this.deviceList.length === 0) {
          // Only fetch active devices if we haven't loaded them yet
          await this.getOrgDevices({
            orgName: this.organization,
            isArchived: false,
          });
          this.deviceList = [...this.devices];
        }

        this.isArchived = newStatus;
      } catch (error) {
        console.error('Error toggling archived view:', error);
        this.$message.error('Failed to load devices. Please try again.');
      } finally {
        this.loading = false;
      }
    },
    async checkPermissionsState() {
      try {
        const microphoneStatus = await navigator.permissions.query({
          name: 'microphone',
        });
        const cameraStatus = await navigator.permissions.query({
          name: 'camera',
        });

        if (
          microphoneStatus.state === 'denied' ||
          microphoneStatus.state === 'prompt' ||
          cameraStatus.state === 'denied' ||
          cameraStatus.state === 'prompt'
        ) {
          this.requestPermissions();
        } else {
          // Permissions are already granted
        }
      } catch (error) {
        console.log('Error checking media permissions:', error);
      }
    },
    async requestPermissions() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        // Stop the stream to avoid using resources unnecessarily
        stream.getTracks().forEach((track) => track.stop());
        await this.checkPermissionsState(); // Update permissions after requesting
      } catch (err) {
        console.log('Permissions denied:', err);
      }
    },
    formatDateWithCurrentTime(date) {
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      };
      const formattedDate = new Intl.DateTimeFormat('en-US', options).format(
        date
      );
      return formattedDate.replace(' PM', '').replace(' GMT+5', '');
    },

    sortDevicesCallback(serialNumber, starred) {
      if (this.activeDevices && this.activeDevices[serialNumber]) return 0;
      if (starred === true) return 1;
      else return 2;
    },

    sortDevices(devices, checkCondition) {
      return devices.reduce(
        (acc, e) => {
          acc[checkCondition(e.Serial_number, e.starred)].push(e);
          return acc;
        },
        [[], [], []]
      );
    },

    getIdToAttributeMapOfList(list, attributeName) {
      return list.reduce((res, el) => {
        res[el.id] = el[attributeName];
        return res;
      }, {});
    },

    async getAllDevices() {
      this.loading = true;
      if (this.devices.length == 0) {
        await this.getOrgDevices(this.organization);
      } else {
        this.deviceList = [...this.devices];
        // this.startLoadingMoreDevices()
      }
      this.loading = false;
    },

    startLoadingMoreDevices() {
      this.intervalId = setInterval(() => {
        this.loadMoreDevices();
      }, 2000);
    },

    async loadMoreDevices() {
      if (this.deviceList.length >= this.devices.length) {
        return;
      }

      this.isLoadingMore = true;

      // Simulate async data fetch
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const nextBatch = this.devices.slice(
        this.deviceList.length,
        this.deviceList.length + 10
      );
      this.deviceList = this.deviceList.concat(nextBatch);
      this.isLoadingMore = false;
    },

    async getTasksList() {
      const [error, data] = await TaskService.fetchTasks(false);
      if (error) return;
      this.tasksList = data;
    },

    async getActiveDevicesStats() {
      const currentDate = `${dayjs(new Date().toISOString()).format(
        dateFormat
      )}`;
      const [error, data] = await DeviceService.getDeviecesStats(false, {
        current_date: currentDate,
      });
      if (error || !data.length) return;

      const stats = data.reduce((res, device) => {
        res[`${device.serial_number}${device.task_id}`] = { ...device.stats };
        return res;
      }, {});
      this.devicesStats = stats;
    },

    setDeviceStatsInterval() {
      this.deviceStatsPolling = setInterval(() => {
        this.getActiveDevicesStats();
      }, 10000);
    },

    sortActiveDevicesByDisplayName(active) {
      active.sort((a, b) => {
        const firstDevice = a.display_name?.toLowerCase();
        const secondDevice = b.display_name?.toLowerCase();
        if (firstDevice === null && secondDevice === null) return 0;
        if (firstDevice === null) return 1;
        if (secondDevice === null) return -1;
        if (firstDevice < secondDevice) return -1;
        if (firstDevice > secondDevice) return 1;
        return 0;
      });
    },

    handleImageError() {
      this.isImageLoaded = false;
    },

    handleDeviceArchived(archivedDevice) {
      // Remove the archived device from the active device list
      this.deviceList = this.deviceList.filter(
        (device) => device.id !== archivedDevice.id
      );

      // Add the device to the archived devices list (prevent duplication)
      const existingIndex = this.archivedDeviceList.findIndex(
        (device) => device.id === archivedDevice.id
      );
      if (existingIndex === -1) {
        this.archivedDeviceList.push(archivedDevice);
      }

      console.log('Device archived:', archivedDevice);
      this.$message.success(
        `${
          archivedDevice.display_name || archivedDevice.Serial_number
        } has been moved to archived devices.`
      );
    },

    handleDeviceUnarchived(unarchivedDevice) {
      // Remove the unarchived device from the archived device list
      this.archivedDeviceList = this.archivedDeviceList.filter(
        (device) => device.id !== unarchivedDevice.id
      );

      // Add the device to the active devices list (prevent duplication)
      const existingIndex = this.deviceList.findIndex(
        (device) => device.id === unarchivedDevice.id
      );
      if (existingIndex === -1) {
        this.deviceList.push(unarchivedDevice);
      }

      console.log('Device unarchived:', unarchivedDevice);
      this.$message.success(
        `${
          unarchivedDevice.display_name || unarchivedDevice.Serial_number
        } has been moved to active devices.`
      );
    },
  },
};
</script>
<style>
.home-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f0f2f5;
  /* padding-top: 60px; Adjust for fixed header */
}

.home-banner {
  width: 100%;
  height: 15vh;
  background: linear-gradient(135deg, #1890ff, #6f42c1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}

.banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  max-width: 1400px;
  margin: 0 auto;
}

.banner-left {
  display: flex;
  align-items: center;
}

.banner-logo {
  width: 100%;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 20px;
  /* box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1); */
}

.banner-title {
  color: white;
  margin-bottom: 0;
}

.banner-right {
  display: flex;
  align-items: center;
}

.archive-toggle-button {
  background: linear-gradient(135deg, #1890ff, #6f42c1);
  border: none;
  border-radius: 25px;
  box-shadow: 0 6px 20px rgba(24, 144, 255, 0.4);
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-width: 200px;
}

.archive-toggle-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}

.archive-toggle-button:hover {
  background: linear-gradient(135deg, #6f42c1, #1890ff);
  box-shadow: 0 8px 25px rgba(24, 144, 255, 0.5);
  transform: translateY(-2px);
}

.archive-toggle-button:hover::before {
  left: 100%;
}

.archive-toggle-button:active {
  transform: translateY(0);
  box-shadow: 0 4px 15px rgba(24, 144, 255, 0.4);
}

.skeleton-container {
  padding: 24px;
}

.skeleton-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.skeleton-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.cards-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  transform: translateY(-35px);
  position: relative;
  z-index: 99;
}

.cards-wrapper {
  height: 100%;
  overflow-y: auto;
}

.no-active-device {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.no-active-device > .anticon-laptop {
  font-size: 3em;
}

@media only screen and (max-width: 576px) {
  .cards-container {
    transform: translateY(-40px);
  }
}
</style>
